/* Keep product feedback first; the native app owns review records and submission. */
(() => {
  function init(scope) {
    // Keep the original app block editable; move the same live widget only on the storefront.
    if (!window.Shopify?.designMode && scope.dataset.reviewProductId) {
      const position = document.querySelector('[data-product-reviews-position="' + scope.dataset.reviewProductId + '"]');
      if (position) position.replaceWith(scope);
    }
    if (scope.dataset.reviewPreferenceReady) return;
    scope.dataset.reviewPreferenceReady = 'true';
    let selectedInitialTab = false;
    const controls = scope.querySelector('[data-review-carousel-controls]');
    const tools = scope.querySelector('[data-review-tools]');
    const storeNote = scope.querySelector('[data-review-store-note]');
    const ratingSummary = scope.querySelector('[data-review-rating-summary]');
    const syncRatingSummary = (heading, isStore) => {
      if (!ratingSummary || !heading) return;
      if (ratingSummary.parentElement !== heading) heading.append(ratingSummary);
      const data = window.jdgm?.data?.reviewWidget?.[scope.dataset.reviewProductId];
      const count = Number(data?.number_of_reviews), average = Number(data?.average_rating);
      const rows = Array.isArray(data?.histogram) ? data.histogram : [];
      const valid = Number.isInteger(count) && count > 0 && average >= 1 && average <= 5 && rows.length === 5 && new Set(rows.map(row => row.rating)).size === 5 && rows.every(row => Number.isInteger(row.rating) && row.rating >= 1 && row.rating <= 5 && Number.isInteger(row.frequency) && row.frequency >= 0) && rows.reduce((sum,row) => sum + row.frequency,0) === count;
      ratingSummary.hidden = isStore || !valid;
      scope.toggleAttribute('data-review-summary-ready', !ratingSummary.hidden);
      if (ratingSummary.hidden) return;
      const signature = JSON.stringify([count,average,rows]);
      if (ratingSummary.dataset.ratingSignature === signature) return;
      ratingSummary.dataset.ratingSignature = signature;
      ratingSummary.querySelector('[data-review-average]').textContent = average.toFixed(1);
      ratingSummary.setAttribute('aria-label', average.toFixed(1) + ' out of 5 from ' + count + ' product reviews');
      ratingSummary.querySelector('[data-review-average-stars]').style.setProperty('--rating-fill', average / 5 * 100 + '%');
      ratingSummary.querySelector('[data-review-total]').textContent = count + (count === 1 ? ' product review' : ' product reviews');
      for (const row of rows) {
        const target = ratingSummary.querySelector('[data-review-star-row="' + row.rating + '"]');
        const progress = target.querySelector('progress');
        progress.max = count; progress.value = row.frequency;
        progress.setAttribute('aria-label', row.rating + '-star reviews: ' + row.frequency + ' of ' + count);
        target.querySelector('[data-review-star-count]').textContent = row.frequency;
      }
    };
    let activeList;
    let excerptId = 0;
    const syncExcerpts = () => {
      scope.querySelectorAll('[data-review-excerpt]').forEach(body => {
        const button = body.parentElement.querySelector('[data-review-expand]');
        if (button) button.hidden = body.dataset.reviewExcerpt !== 'expanded' && body.scrollHeight <= body.clientHeight + 1;
      });
    };
    const syncControls = () => {
      if (!controls) return;
      const remaining = activeList ? activeList.scrollWidth - activeList.clientWidth : 0;
      controls.hidden = remaining < 2;
      controls.querySelector('[data-review-direction="-1"]').disabled = !activeList || activeList.scrollLeft < 2;
      controls.querySelector('[data-review-direction="1"]').disabled = !activeList || activeList.scrollLeft >= remaining - 2;
    };
    const resize = new ResizeObserver(() => { syncControls(); syncExcerpts(); });
    controls?.addEventListener('click', event => {
      const button = event.target.closest('[data-review-direction]');
      const card = activeList?.querySelector('.jm-review-item');
      if (!button || !card) return;
      const gap = parseFloat(getComputedStyle(activeList).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      const visible = Math.max(1, Math.floor((activeList.clientWidth + gap + 1) / step));
      activeList.scrollBy({ left: Number(button.dataset.reviewDirection) * step * visible, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    });
    scope.addEventListener('click', event => {
      const button = event.target.closest('[data-review-expand]');
      if (!button) return;
      const body = document.getElementById(button.getAttribute('aria-controls'));
      if (!body || !scope.contains(body)) return;
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      body.dataset.reviewExcerpt = expanded ? 'expanded' : 'collapsed';
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? 'Read less' : 'Read more';
    });
    scope.addEventListener('keydown', event => {
      if (event.key === 'Escape' && tools?.open) { tools.open = false; tools.querySelector('summary').focus(); }
    });
    scope.addEventListener('change', event => {
      if (event.target.matches('.jm-sort-filter__select')) {
        scope.querySelector('.jdgm-review-list')?.scrollTo({ left: 0 });
        if (tools) tools.open = false;
      }
    });
    const update = () => {
      const list = scope.querySelector('.jdgm-review-list');
      if (list !== activeList) {
        activeList?.removeEventListener('scroll', syncControls);
        resize.disconnect();
        activeList = list;
        if (list) {
          list.addEventListener('scroll', syncControls, { passive: true });
          resize.observe(list);
        }
      }
      if (list) {
        list.tabIndex = 0;
        list.setAttribute('role', 'group');
        list.setAttribute('aria-label', 'Review cards');
        const first = list.querySelector('.jm-review-item')?.getAttribute('review-uuid');
        if (first && list.dataset.reviewCarouselFirst !== first) {
          list.scrollLeft = 0;
          list.dataset.reviewCarouselFirst = first;
        }
      }
      const heading = scope.querySelector('.jm-review-widget__header>.jm-cluster');
      const actions = scope.querySelector('.jm-review-widget-minimal-header__filter-container');
      if (controls && actions && controls.parentElement !== actions) actions.append(controls);
      // Keep the original filter controls and their app events inside a compact disclosure.
      const filters = scope.querySelector('.jm-filters');
      if (tools && heading && filters) {
        if (tools.parentElement !== heading) heading.append(tools);
        if (filters.parentElement !== tools) tools.append(filters);
      }
      scope.querySelectorAll('.jm-review-content__body').forEach(body => {
        if (body.closest('.jm-review-content').querySelector('button')) return;
        body.id ||= scope.id + '-excerpt-' + (++excerptId);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'brand-product-reviews__read-more';
        button.dataset.reviewExpand = '';
        button.setAttribute('aria-controls', body.id);
        button.setAttribute('aria-expanded', 'false');
        button.textContent = 'Read more';
        button.hidden = true;
        body.dataset.reviewExcerpt = 'collapsed';
        body.after(button);
      });
      requestAnimationFrame(() => { syncControls(); syncExcerpts(); });
      const storeTab = scope.querySelector('#jdgm-tab_shop_reviews');
      const productTab = scope.querySelector('#jdgm-tab_product_reviews');
      if (!storeTab || !productTab) return;
      if (storeTab.textContent.includes('Store reviews')) storeTab.textContent = storeTab.textContent.replace('Store reviews', 'Store experience');
      if (!selectedInitialTab) {
        selectedInitialTab = true;
        if (productTab.getAttribute('aria-selected') !== 'true') {
          const position = { left: window.scrollX, top: window.scrollY, behavior: 'instant' };
          productTab.click();
          // Native tab activation scrolls the widget into view. Initial preference must not move the shopper.
          requestAnimationFrame(() => window.scrollTo(position));
        }
      }
      const isStore = storeTab.getAttribute('aria-selected') === 'true';
      scope.dataset.reviewScope = isStore ? 'store' : 'product';
      syncRatingSummary(heading, isStore);
      const rating = scope.querySelector('.jm-average-rating-display');
      if (storeNote && heading) {
        const noteParent = rating?.parentElement || heading;
        if (storeNote.parentElement !== noteParent) noteParent.append(storeNote);
        storeNote.hidden = !isStore;
      }
      const count = (isStore ? storeTab : productTab).textContent.match(/[（(]([\d,]+)[)）]/);
      if (tools) tools.hidden = !filters || !!(count && Number(count[1].replaceAll(',', '')) === 0);
    };
    new MutationObserver(update).observe(scope, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-selected'] });
    update();
  }
  document.querySelectorAll('[data-product-reviews-first]').forEach(init);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-product-reviews-first]').forEach(init);
  });
})();
